import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { Button } from "@/components/Button";
import { Skeleton } from "@/components/Skeleton";
import { EmptyState } from "@/components/EmptyState";
import { parseDateLocal } from "@/lib/format";
import { HealthBadge } from "@/components/HealthBadge";

type Zoom = "quarter" | "half" | "year";
const PX_PER_DAY: Record<Zoom, number> = { quarter: 4, half: 2, year: 0.9 };
const ROW_H = 40;
const LABEL_W = 200;
const HEADER_H = 28;
const DAY_MS = 86_400_000;

function dayNum(d: Date): number {
  return Math.floor(
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime() / DAY_MS,
  );
}
const STATUS_COLOR: Record<string, string> = {
  active: "#16a34a",
  on_hold: "#d97706",
  archived: "#6b7280",
};

function RoadmapView() {
  const navigate = useNavigate();
  const [zoom, setZoom] = useState<Zoom>("half");
  const [teamFilter, setTeamFilter] = useState("");
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const pxPerDay = PX_PER_DAY[zoom];

  const projectsQ = useQuery({
    queryKey: queryKeys.projects(),
    queryFn: api.listProjects,
  });
  const msQ = useQuery({
    queryKey: queryKeys.allMilestones(),
    queryFn: api.allMilestones,
  });
  const teamsQ = useQuery({ queryKey: ["teams"], queryFn: () => api.teams() });
  const linksQ = useQuery({
    queryKey: ["project-teams"],
    queryFn: () => api.projectTeamLinks(),
  });

  const share = useMutation({
    mutationFn: () => api.createRoadmapShare(),
    onSuccess: (r) => setShareUrl(`${window.location.origin}/r/${r.token}`),
  });

  // Projects associated with the selected team (for the team filter).
  const teamProjectIds = teamFilter
    ? new Set(
        (linksQ.data?.links ?? [])
          .filter((l) => l.teamId === teamFilter)
          .map((l) => l.projectId),
      )
    : null;

  const projects = useMemo(
    () =>
      (projectsQ.data?.projects ?? [])
        .filter((p) => p.status !== "archived")
        .filter((p) => !teamProjectIds || teamProjectIds.has(p.id)),
    [projectsQ.data, teamFilter, linksQ.data],
  );
  const milestones = msQ.data?.milestones ?? [];

  const { rangeStart, totalDays } = useMemo(() => {
    const nums: number[] = [];
    for (const p of projects) {
      nums.push(dayNum(parseDateLocal(p.createdAt.slice(0, 10))!));
      if (p.targetDate) nums.push(dayNum(parseDateLocal(p.targetDate)!));
    }
    for (const m of milestones)
      if (m.dueDate) nums.push(dayNum(parseDateLocal(m.dueDate)!));
    const today = dayNum(new Date());
    nums.push(today - 30, today + 120);
    const min = Math.min(...nums) - 10;
    const max = Math.max(...nums) + 20;
    return { rangeStart: min, totalDays: max - min + 1 };
  }, [projects, milestones]);

  const xForDay = (day: number) => (day - rangeStart) * pxPerDay;
  const xForDate = (s: string) => xForDay(dayNum(parseDateLocal(s)!));
  const width = totalDays * pxPerDay;
  const bodyH = Math.max(projects.length * ROW_H, ROW_H);
  const todayX = xForDay(dayNum(new Date()));

  // Quarter ticks (Jan/Apr/Jul/Oct 1).
  const quarterTicks = useMemo(() => {
    const ticks: { x: number; label: string }[] = [];
    const start = new Date((rangeStart + 0.5) * DAY_MS);
    const d = new Date(start.getFullYear(), 0, 1);
    while (dayNum(d) <= rangeStart + totalDays) {
      if (dayNum(d) >= rangeStart) {
        const q = Math.floor(d.getMonth() / 3) + 1;
        ticks.push({
          x: xForDay(dayNum(d)),
          label: `Q${q} ${d.getFullYear()}`,
        });
      }
      d.setMonth(d.getMonth() + 3);
    }
    return ticks;
  }, [rangeStart, totalDays, pxPerDay]);

  const projectRowY = new Map(projects.map((p, i) => [p.id, i * ROW_H]));

  return (
    <div className="pg-wrap" style={{ padding: "22px 30px 60px" }}>
      <div className="rm-head">
        <h1 className="pg-title" style={{ margin: 0 }}>
          Roadmap
        </h1>
        <div className="seg" role="group" aria-label="Zoom">
          {(["quarter", "half", "year"] as Zoom[]).map((z) => (
            <button
              key={z}
              type="button"
              className={zoom === z ? "seg-btn on" : "seg-btn"}
              aria-pressed={zoom === z}
              onClick={() => setZoom(z)}
            >
              {z === "half"
                ? "Half-year"
                : z.charAt(0).toUpperCase() + z.slice(1)}
            </button>
          ))}
        </div>
        {(teamsQ.data?.teams ?? []).length > 0 && (
          <select
            className="select"
            style={{ width: 150, height: 32 }}
            value={teamFilter}
            onChange={(e) => setTeamFilter(e.target.value)}
            aria-label="Filter by team"
          >
            <option value="">All teams</option>
            {(teamsQ.data?.teams ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        )}
        <Button
          variant="secondary"
          onClick={() => share.mutate()}
          disabled={share.isPending}
        >
          Share
        </Button>
      </div>
      {shareUrl && (
        <div className="rm-share">
          <span className="t-sm t-muted">Read-only link:</span>
          <input
            className="input"
            readOnly
            value={shareUrl}
            aria-label="Share link"
          />
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void navigator.clipboard.writeText(shareUrl)}
          >
            Copy
          </Button>
        </div>
      )}

      {projectsQ.isLoading ? (
        <Skeleton count={5} height={40} />
      ) : projects.length === 0 ? (
        <EmptyState
          icon="arrow"
          title="No active initiatives"
          caption="Projects with a target date appear here as a quarter-scaled roadmap."
        />
      ) : (
        <div className="tl-scroll" style={{ marginTop: 14 }}>
          <div className="tl-grid" style={{ width: LABEL_W + width }}>
            <div className="tl-labels" style={{ width: LABEL_W }}>
              <div className="tl-corner" style={{ height: HEADER_H }} />
              {projects.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className="tl-row-label"
                  style={{ height: ROW_H }}
                  onClick={() =>
                    navigate({
                      to: "/projects/$projectId",
                      params: { projectId: p.id },
                    })
                  }
                  title={p.name}
                >
                  <HealthBadge project={p} showLabel={false} />
                  <span style={{ marginLeft: 6 }}>{p.name}</span>
                </button>
              ))}
            </div>

            <div className="tl-canvas" style={{ width }}>
              <div className="tl-header" style={{ height: HEADER_H, width }}>
                {quarterTicks.map((t, i) => (
                  <div key={i} className="tl-month" style={{ left: t.x }}>
                    {t.label}
                  </div>
                ))}
              </div>
              <div className="tl-body" style={{ height: bodyH, width }}>
                {quarterTicks.map((t, i) => (
                  <div key={i} className="tl-gridline" style={{ left: t.x }} />
                ))}
                {todayX >= 0 && todayX <= width && (
                  <div
                    className="tl-today"
                    style={{ left: todayX }}
                    title="Today"
                  />
                )}

                {/* project bars */}
                {projects.map((p) => {
                  const startDay = parseDateLocal(p.createdAt.slice(0, 10))!;
                  const left = xForDate(p.createdAt.slice(0, 10));
                  const endX = p.targetDate
                    ? xForDate(p.targetDate) + pxPerDay
                    : left + 21 * pxPerDay;
                  const y = projectRowY.get(p.id)!;
                  void startDay;
                  return (
                    <div
                      key={p.id}
                      className="rm-bar"
                      style={{
                        top: y + 9,
                        left,
                        width: Math.max(12, endX - left),
                        background:
                          p.color ?? STATUS_COLOR[p.status] ?? "#2563eb",
                      }}
                      title={`${p.name}${p.targetDate ? ` — target ${p.targetDate}` : ""}`}
                    >
                      <span className="rm-bar-label">{p.name}</span>
                    </div>
                  );
                })}

                {/* milestone markers on each project's row */}
                {milestones.map((m) => {
                  if (!m.dueDate) return null;
                  const y = projectRowY.get(m.projectId);
                  if (y == null) return null;
                  const done = m.taskCount > 0 && m.doneCount === m.taskCount;
                  return (
                    <div
                      key={m.id}
                      className="rm-ms"
                      style={{
                        left: xForDate(m.dueDate),
                        top: y + 6,
                        color: done ? "#157049" : "#7e22ce",
                      }}
                      title={`${m.name} — ${m.dueDate} (${m.doneCount}/${m.taskCount})`}
                    >
                      ◆
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute("/roadmap")({
  component: RoadmapView,
});
